import Button from "@/v2-ui/button"

export type StatusErrorProps = {
  // Last fetch error message, e.g. "HTTP 502" or "Network error".
  // Optional because we may render this surface speculatively
  // (e.g. SSR) before any fetch has had a chance to fail.
  error: string | null
  // True while a refresh is in flight. The Retry button uses this
  // to render its built-in spinner and disable itself so the
  // customer can't queue a stack of retries on a slow connection.
  isRefreshing: boolean
  // Imperative kicker to force an immediate poll. Wired up to
  // `useStatusQuery#refetch` at the page level; kept as a prop
  // here so this component stays presentational and testable
  // without a hook.
  onRetry: () => void
}

// "Status page unavailable" surface. Rendered only when we have
// no payload at all (very first load, every retry has failed).
// The footer pip handles the soft case where a stale payload is
// still on screen and only the latest refresh failed -- this
// page is exclusively the no-data fallback.
//
// We expose an explicit Retry button rather than relying on the
// background poll so a customer who lands during a transient
// outage can recover in a click instead of waiting the full
// minute the poll cadence implies.
function StatusError(props: Readonly<StatusErrorProps>) {
  const { error, isRefreshing, onRetry } = props
  return (
    <div
      role="alert"
      aria-live="assertive"
      className="flex m-auto max-w-md flex-col items-center gap-3 px-6 py-10 text-center"
    >
      <ErrorMark />

      <h1 className="m-0 text-xl font-semibold tracking-tight">
        We can&apos;t reach the status service
      </h1>

      <p className="m-0 text-sm text-muted-foreground">
        The page will retry automatically every minute. You can also try
        refreshing now.
      </p>

      <div className="flex items-center gap-2 pt-2">
        <Button
          type="button"
          variant="primary"
          size="sm"
          isLoading={isRefreshing}
          onClick={onRetry}
        >
          {isRefreshing ? "Retrying…" : "Try again"}
        </Button>
      </div>

      {error && (
        <p className="m-0 text-xs text-muted-foreground/80">
          Last error: <span className="font-mono">{error}</span>
        </p>
      )}
    </div>
  )
}

// Decorative warning glyph above the headline. Inline SVG so the
// fallback works even if the icon font / sprite hasn't loaded
// (the most likely cause of *being* on the fallback in the first
// place is the network being uncooperative).
function ErrorMark() {
  return (
    <div
      aria-hidden
      className="flex size-10 items-center justify-center rounded-full border border-amber-400/40 bg-amber-400/10 text-amber-300"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="size-5"
      >
        <path d="M12 9v4" />
        <path d="M12 17h.01" />
        <path d="m4.93 19 6.35-11.79a1 1 0 0 1 1.76 0L19.4 19a1 1 0 0 1-.88 1.5H5.81A1 1 0 0 1 4.93 19Z" />
      </svg>
    </div>
  )
}

export default StatusError
